import React, { MouseEvent, useMemo, useState } from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { getTagVerdict, createReportsSearchLink, getColorSchemaClass } from '../helpers'
import { SignalVerdict, TagType } from '../types'
import Button from 'app/modules/shared/components/button'
import { deduplicateTags } from '../helpers'
import { useResponsive } from 'app/modules/shared/hooks/use-responsive'

type TagProps = {
  tag: TagType
  className?: string
  noColor?: boolean
  exporting?: boolean
}
export function Tag(props: TagProps): JSX.Element {
  const { tag, className, noColor, exporting } = props

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    exporting && e.preventDefault()
    e.stopPropagation()
  }

  const name = tag.tag.name
  const verdict = getTagVerdict(tag)
  const descriptions = tag.tag.descriptions
  const searchLink = createReportsSearchLink({ tag: name })
  const colorClass = getColorSchemaClass(verdict, 'secondary')
  const tagClass = `${className ?? ''} ${noColor ? '' : colorClass}`

  const [description, source] = useMemo(() => {
    if (!descriptions?.[0]) {
      return ['', null]
    }

    const synonyms = tag.tag.synonyms
    const desc: string = descriptions[0].description
    const cluster = descriptions[0].cluster
    const type: string = cluster.type ?? ''
    const authors = Array.isArray(cluster.authors) ? cluster.authors.join(', ') : cluster.authors

    const source =
      synonyms?.length || type || authors ? (
        <Popover.Body>
          {synonyms?.length ? (
            <>
              <b>Synonyms</b>:&nbsp;{synonyms.join(', ')}
              <br />
            </>
          ) : null}
          {type && (
            <>
              <b>Source</b>:&nbsp;MISP Galaxy ({type})
            </>
          )}
          {!type && authors && (
            <>
              <b>Authors</b>:&nbsp;{authors}
            </>
          )}
        </Popover.Body>
      ) : null

    return [desc, source]
  }, [descriptions])

  return (
    <a
      className={`badge badge-fs badge-fs-tag text-lower ${tagClass} ${exporting ? 'cursor-default' : ''}`}
      href={exporting ? 'javascript:;' : searchLink}
      target={exporting ? '_self' : '_blank'}
      onClick={handleClick}
      aria-label="Tag search"
      rel="noreferrer"
    >
      {name}

      {!exporting && description ? (
        <OverlayTrigger
          placement="auto"
          aria-hidden={true}
          overlay={
            <Popover id={`${name}-info`} title={description} className="responsive-popover">
              <Popover.Header>{description}</Popover.Header>
              {source}
            </Popover>
          }
          delay={300}
        >
          {({ ref, ...triggerHandler }) => (
            <i {...triggerHandler} ref={ref} className="ds3-icon ds3-information ms-1" />
          )}
        </OverlayTrigger>
      ) : null}
    </a>
  )
}

type TagListProps = {
  tags: TagType[] | string[]
  sort?: boolean
  short?: boolean
  tagClass?: string
  exporting?: boolean
  allowFull?: boolean
  size?: 'lg'
  showLimit?: number
  allowShort?: boolean
}
export function TagList(props: TagListProps): JSX.Element | null {
  const { tags, sort, short, tagClass, exporting, size, showLimit, allowShort, allowFull = true } = props

  const [full, setFull] = useState(!short)
  const verdicts = Object.values(SignalVerdict)
  // Max limit depends on screen size
  const { shortLimit } = useResponsive(
    [
      { maxWidth: 767, shortLimit: 5 },
      { maxWidth: 991, shortLimit: 10 },
    ],
    { shortLimit: showLimit ?? 15 }
  )

  const toggleFull = (e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    allowFull && setFull((flag) => !flag)
  }

  const { sorted, roots } = useMemo(() => {
    const normalized: TagType[] = tags.map((tag) =>
      typeof tag === 'string' ? { source: '', tag: { name: tag } } : tag
    )
    const deduplicated = deduplicateTags(normalized)
    const [roots, nonRoots] = partitionRoots(deduplicated)

    const filtered = nonRoots.filter(
      (tag, idx) => nonRoots.findIndex((value) => value.tag.name === tag.tag.name) === idx
    )

    // Sorting is available on BE but can't be removed due to old data in database
    if (sort) {
      filtered.sort((a, b) => {
        let comp = (b.tag.descriptions?.length ? 1 : 0) - (a.tag.descriptions?.length ? 1 : 0)
        if (comp === 0) {
          const verdictA = getTagVerdict(a)
          const verdictB = getTagVerdict(b)
          comp = verdicts.indexOf(verdictA) - verdicts.indexOf(verdictB)
        }
        return comp
      })
    }

    return { sorted: filtered, roots }
    // eslint-disable-next-line
  }, [tags, sort])

  const allTags = useMemo(() => {
    return [...roots, ...sorted]
  }, [roots, sorted])

  // No shortening needed when full list is shown or short limit is more than the number of items in allTags list
  const shortened = useMemo(
    () => (full || allTags.length <= shortLimit ? allTags : allTags.slice(0, shortLimit)),
    [allTags, full, shortLimit]
  )

  // For displaying in tooltip if allowFull is false
  const moreNames = useMemo(() => {
    if (allowFull) {
      return undefined
    }

    const names: string[] = []
    for (let i = shortLimit; i < allTags.length; ++i) {
      names.push(allTags[i].tag.name)
    }

    return names.length ? names.join(', ') : undefined
  }, [allTags, shortLimit])

  // Determine color class for more button based on hidden malicious tags
  const moreButtonColorClass = useMemo(() => {
    const hiddenTags = allTags.slice(shortLimit)
    if (hiddenTags.some((tag) => getTagVerdict(tag) === SignalVerdict.Malicious)) return 'colors-malicious'
    if (hiddenTags.some((tag) => getTagVerdict(tag) === SignalVerdict.Likely_malicious))
      return 'colors-likely_malicious'
    return ''
  }, [allTags, shortLimit])

  const sizeClass = size === 'lg' ? 'tag-lg' : ''
  const tagClassStr = `${sizeClass} ${tagClass ?? ''}`
  const colorClass = !full ? `colors-schema-secondary ${moreButtonColorClass}` : ''

  return (
    <div className={`tag-list ${size === 'lg' ? 'tag-list-lg' : ''}`}>
      {/* all tags, with different class for roots */}
      {shortened.map((tag, idx) => {
        const isRoot = tag.source === 'MEDIA_TYPE' && tag.isRootTag
        const tagClass = isRoot ? `badge-fs-info ${tagClassStr}` : tagClassStr
        return (
          <Tag
            tag={tag}
            key={`${isRoot ? 'root' : 'tag'}-${idx}`}
            className={tagClass}
            noColor={isRoot}
            exporting={exporting}
          />
        )
      })}

      {/* more button */}
      {(allowShort && allTags.length > shortLimit) || (shortened.length < allTags.length && !full) ? (
        <Button
          variant=""
          onClick={toggleFull}
          className={`badge more-button ${allowFull ? '' : 'with-tooltip'} ${sizeClass} ${colorClass}`}
          aria-label="More tags"
          tooltip={moreNames}
        >
          {!full ? `+${allTags.length - shortened.length}` : <i className="ds3-icon ds3-chevron-up" />}
        </Button>
      ) : null}
    </div>
  )
}

// Partition root tags into the first array and non-root ones into the second
const partitionRoots = (tags: TagType[]) => {
  return tags.reduce(
    (acc, cur) => {
      const index = cur.source === 'MEDIA_TYPE' && cur.isRootTag ? 0 : 1
      acc[index].push(cur)
      return acc
    },
    [[], []] as [TagType[], TagType[]]
  )
}
